import { useState } from 'react';
import {
  DevSettings,
  getDevSettings,
  saveDevSettings,
  resetDevSettings
} from '../../../utils/storage';
import type { MessageState } from '../types';
import { DEFAULT_DAILY_ALERT_THRESHOLD_USD } from '../../../constants/ui';

export function useApplicationSettings(
  showMessage: (type: MessageState['type'], text: string) => void
) {
  const [devSettings, setDevSettings] = useState<DevSettings>(() => getDevSettings());
  const [showHolidays, setShowHolidays] = useState(() => {
    const saved = localStorage.getItem('showHolidayOverlays');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [notificationThreshold, setNotificationThreshold] = useState(() => {
    const saved = localStorage.getItem('notificationThreshold');
    if (saved === null) return DEFAULT_DAILY_ALERT_THRESHOLD_USD;
    const parsed = Number(saved);
    return Number.isFinite(parsed) && parsed >= 0
      ? parsed
      : DEFAULT_DAILY_ALERT_THRESHOLD_USD;
  });

  const loadDevSettings = () => {
    const settings = getDevSettings();
    setDevSettings(settings);
  };

  const handleDevSettingsChange = (newSettings: Partial<DevSettings>) => {
    const updatedSettings = { ...devSettings, ...newSettings };
    setDevSettings(updatedSettings);
    const saved = saveDevSettings(updatedSettings);
    if (saved) {
      showMessage('info', 'Application settings saved');
      window.dispatchEvent(new CustomEvent('devSettingsChanged'));
    } else {
      showMessage('error', 'Failed to save application settings');
    }
  };

  const handleResetDevSettings = () => {
    if (window.confirm('Reset application settings to default values?')) {
      const reset = resetDevSettings();
      if (reset) {
        loadDevSettings();
        showMessage('info', 'Application settings reset to default');
        window.dispatchEvent(new CustomEvent('devSettingsChanged'));
      } else {
        showMessage('error', 'Failed to reset application settings');
      }
    }
  };

  const handleToggleHolidays = (value: boolean) => {
    setShowHolidays(value);
    localStorage.setItem('showHolidayOverlays', JSON.stringify(value));
    window.dispatchEvent(new CustomEvent('holidaySettingsChanged'));
  };

  const handleThresholdChange = (value: number) => {
    setNotificationThreshold(value);
    localStorage.setItem('notificationThreshold', value.toString());
    window.dispatchEvent(new CustomEvent('notificationThresholdChanged'));
  };

  return {
    devSettings,
    showHolidays,
    notificationThreshold,
    handleDevSettingsChange,
    handleResetDevSettings,
    handleToggleHolidays,
    handleThresholdChange
  };
}
